export type DimMode = "exact" | "min" | "range" | "portrait" | "any";

export interface DimSpec {
  width?: number;
  height?: number;
  minWidth?: number;
  minHeight?: number;
  minRatio?: number;
  maxRatio?: number;
  aspectRatio?: string;
  label?: string;
}

export interface DimResult {
  width: number;
  height: number;
  ok: boolean;
  message?: string;
}

export function loadImageDims(file: File): Promise<{ width: number; height: number }> {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      const w = img.naturalWidth || img.width;
      const h = img.naturalHeight || img.height;
      URL.revokeObjectURL(url);
      if (!w || !h) {
        reject(new Error("Could not read image dimensions"));
        return;
      }
      resolve({ width: w, height: h });
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error("Failed to decode image"));
    };
    img.src = url;
  });
}

export function describeSpec(spec: DimSpec, mode: DimMode = "exact"): string {
  if (spec.aspectRatio) return spec.aspectRatio;
  if (mode === "portrait") {
    return spec.width && spec.height
      ? `${spec.width} x ${spec.height} px (portrait, ${spec.width}:${spec.height})`
      : "portrait orientation";
  }
  if (mode === "exact" && spec.width && spec.height) {
    const ratio = (spec.width / spec.height).toFixed(2);
    return `${spec.width} x ${spec.height} px (${ratio}:1)`;
  }
  if (mode === "min" && spec.minWidth && spec.minHeight) {
    return `at least ${spec.minWidth} x ${spec.minHeight} px`;
  }
  if (mode === "range" && spec.minRatio && spec.maxRatio) {
    return `aspect ratio between ${spec.minRatio.toFixed(2)}:1 and ${spec.maxRatio.toFixed(2)}:1`;
  }
  return "recommended size";
}

export async function validateImageDimensions(
  file: File,
  spec: DimSpec,
  mode: DimMode = "exact",
): Promise<DimResult> {
  if (mode === "any") {
    const dims = await loadImageDims(file);
    return { ...dims, ok: true };
  }

  const dims = await loadImageDims(file);
  const { width, height } = dims;
  const label = spec.label || "image";

  const check = (cond: boolean, msg: string): DimResult => {
    if (cond) return { width, height, ok: true };
    return {
      width,
      height,
      ok: false,
      message: `${label}: ${msg} (detected ${width} x ${height}px)`,
    };
  };

  if (mode === "exact") {
    if (spec.width == null || spec.height == null) {
      return { width, height, ok: true };
    }
    return check(
      width === spec.width && height === spec.height,
      `expected exactly ${spec.width} x ${spec.height} px`,
    );
  }

  if (mode === "min") {
    return check(
      width >= (spec.minWidth ?? 0) && height >= (spec.minHeight ?? 0),
      `expected at least ${spec.minWidth} x ${spec.minHeight} px`,
    );
  }

  if (mode === "range") {
    const ratio = width / height;
    return check(
      ratio >= (spec.minRatio ?? 0) && ratio <= (spec.maxRatio ?? Infinity),
      `expected aspect ratio between ${spec.minRatio} and ${spec.maxRatio}`,
    );
  }

  if (mode === "portrait") {
    return check(
      height > width,
      `expected portrait orientation (height greater than width)`,
    );
  }

  return { width, height, ok: true };
}

export const ALLOWED_IMAGE_MIME = [
  "image/jpeg",
  "image/jpg",
  "image/png",
  "image/gif",
  "image/webp",
];

export const MAX_IMAGE_BYTES = 5 * 1024 * 1024;
